import { APIFetchError } from "@cur8/api-client";

/** RFC 7807 problem response returned by the backend on 4XX. */
export type ProblemDetails = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  traceId?: string;
};

/** What the server told us about a failed request, as far as it got. */
export type ProblemInfo = {
  status: number;
  detail?: string;
  traceId?: string;
};

/**
 * Read the problem body off a failed request, whatever the status. The backend answers
 * 5XX with a problem body too, and its traceId is the only way to find the request in
 * App Insights, so do not restrict this to 4XX.
 * Returns undefined when the request never reached the server.
 */
export async function getProblem(error: unknown) {
  if (!(error instanceof APIFetchError)) {
    return undefined;
  }

  const { response } = error;
  const info: ProblemInfo = { status: response.status };

  if (!response.headers.get("content-type")?.includes("json")) {
    return info;
  }

  try {
    // clone so callers keep an unread body; a body can only be read once
    const problem: ProblemDetails = await response.clone().json();
    return {
      ...info,
      detail: problem?.detail?.trim() || undefined,
      traceId: problem?.traceId?.trim() || undefined,
    };
  } catch {
    return info;
  }
}

/**
 * Turn a failed request into something an operator can read, and failing that, something
 * they can quote to support.
 */
export async function describeApiError(fallback: string, error: unknown) {
  const problem = await getProblem(error);

  if (!problem) {
    return fallback;
  }

  if (problem.detail) {
    return `${fallback}: ${problem.detail}`;
  }

  if (problem.traceId) {
    return `${fallback} (${problem.status}). Reference: ${problem.traceId}`;
  }

  return `${fallback} (${problem.status})`;
}
